import React, { useMemo } from 'react';
import { BarChart3, TrendingUp, PieChart, DollarSign, MapPin, Users } from 'lucide-react';

const AdvancedStats = ({ mapData }) => {
  // Calculs statistiques avancés
  const stats = useMemo(() => {
    const chantiers = mapData.chantiers_rcd || [];
    const dommages = mapData.dommages || [];
    const travaux = mapData.travaux_reparation || [];
    const membres = mapData.membres || [];
    const coopers = mapData.coopers || [];

    // Montants totaux
    const montantTotalChantiers = chantiers.reduce((sum, c) => sum + (c.montant_travaux || 0), 0);
    const montantTotalTravaux = travaux.reduce((sum, t) => sum + (t.montant_devis || 0), 0);
    const montantTotalDommages = dommages.reduce((sum, d) => sum + (d.montant_estime || 0), 0);

    // Répartition par statut
    const chantiersParStatut = chantiers.reduce((acc, c) => {
      acc[c.status] = (acc[c.status] || 0) + 1;
      return acc;
    }, {});

    // Répartition par territoire
    const parTerritoire = {};
    
    chantiers.forEach(c => {
      const ville = c.geo_location?.city || 'Non spécifié';
      parTerritoire[ville] = (parTerritoire[ville] || 0) + 1;
    });

    // Top 5 territoires
    const top5Territoires = Object.entries(parTerritoire)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    // Avancement moyen
    const avancementMoyen = chantiers.length > 0
      ? Math.round(chantiers.reduce((sum, c) => sum + (c.avancement_pct || 0), 0) / chantiers.length)
      : 0;

    // Types de dommages
    const typeDommages = dommages.reduce((acc, d) => {
      acc[d.type_dommage] = (acc[d.type_dommage] || 0) + 1;
      return acc;
    }, {});

    // Dispositifs actifs (membres)
    const dispositifs = {};
    membres.forEach(m => {
      (m.dispositifs_actifs || []).forEach(d => {
        dispositifs[d] = (dispositifs[d] || 0) + 1;
      });
    });

    return {
      montantTotalChantiers,
      montantTotalTravaux,
      montantTotalDommages,
      montantTotal: montantTotalChantiers + montantTotalTravaux,
      chantiersParStatut,
      top5Territoires,
      avancementMoyen,
      typeDommages,
      dispositifs,
      nombreTotalMarqueurs: chantiers.length + dommages.length + travaux.length + membres.length + coopers.length
    };
  }, [mapData]);

  const formatEuro = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="advanced-stats-panel">
      <div className="stats-header">
        <BarChart3 className="h-6 w-6" />
        <h2>Statistiques Avancées</h2>
      </div>

      <div className="stats-content">
        {/* Montants */}
        <div className="stat-card-advanced">
          <div className="stat-card-header">
            <DollarSign className="h-5 w-5" />
            <h3>Montants Financiers</h3>
          </div>
          <div className="stat-grid">
            <div className="stat-item-advanced">
              <span className="stat-label-adv">Chantiers RCDS</span>
              <span className="stat-value-adv primary">{formatEuro(stats.montantTotalChantiers)}</span>
            </div>
            <div className="stat-item-advanced">
              <span className="stat-label-adv">Travaux Réparation</span>
              <span className="stat-value-adv warning">{formatEuro(stats.montantTotalTravaux)}</span>
            </div>
            <div className="stat-item-advanced">
              <span className="stat-label-adv">Dommages Estimés</span>
              <span className="stat-value-adv danger">{formatEuro(stats.montantTotalDommages)}</span>
            </div>
            <div className="stat-item-advanced highlight">
              <span className="stat-label-adv">Total Engagé</span>
              <span className="stat-value-adv success">{formatEuro(stats.montantTotal)}</span>
            </div>
          </div>
        </div>

        {/* Top Territoires */}
        <div className="stat-card-advanced">
          <div className="stat-card-header">
            <MapPin className="h-5 w-5" />
            <h3>Top 5 Territoires</h3>
          </div>
          <div className="territory-list">
            {stats.top5Territoires.map(([ville, count], idx) => (
              <div key={ville} className="territory-item">
                <div className="territory-rank">#{idx + 1}</div>
                <div className="territory-info">
                  <span className="territory-name">{ville}</span>
                  <div className="territory-bar">
                    <div 
                      className="territory-bar-fill"
                      style={{ 
                        width: `${(count / stats.top5Territoires[0][1]) * 100}%`,
                        background: `linear-gradient(90deg, #00ffff ${idx * 20}%, #00cccc 100%)`
                      }}
                    />
                  </div>
                </div>
                <span className="territory-count">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Répartition par Statut */}
        <div className="stat-card-advanced">
          <div className="stat-card-header">
            <PieChart className="h-5 w-5" />
            <h3>Chantiers par Statut</h3>
          </div>
          <div className="status-grid">
            {Object.entries(stats.chantiersParStatut).map(([status, count]) => (
              <div key={status} className="status-item">
                <span className={`status-badge ${status}`}>{status}</span>
                <span className="status-count">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Avancement Moyen */}
        <div className="stat-card-advanced">
          <div className="stat-card-header">
            <TrendingUp className="h-5 w-5" />
            <h3>Avancement Global</h3>
          </div>
          <div className="progress-section">
            <div className="progress-value">{stats.avancementMoyen}%</div>
            <div className="progress-bar-container">
              <div 
                className="progress-bar-fill"
                style={{ width: `${stats.avancementMoyen}%` }}
              />
            </div>
            <p className="progress-text">Avancement moyen des chantiers en cours</p>
          </div>
        </div>

        {/* Types de Dommages */}
        {Object.keys(stats.typeDommages).length > 0 && (
          <div className="stat-card-advanced">
            <div className="stat-card-header">
              <h3>⚠️ Types de Dommages</h3>
            </div>
            <div className="damage-types">
              {Object.entries(stats.typeDommages).map(([type, count]) => (
                <div key={type} className="damage-type-item">
                  <span className="damage-type">{type}</span>
                  <span className="damage-count">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dispositifs Actifs */}
        {Object.keys(stats.dispositifs).length > 0 && (
          <div className="stat-card-advanced">
            <div className="stat-card-header">
              <Users className="h-5 w-5" />
              <h3>Dispositifs Actifs</h3>
            </div>
            <div className="dispositif-grid">
              {Object.entries(stats.dispositifs).map(([dispositif, count]) => (
                <div key={dispositif} className="dispositif-item">
                  <span className="dispositif-name">{dispositif}</span>
                  <span className="dispositif-badge">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedStats;
